import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth, useTenantsActions, useTenantsState } from '@frontegg/react'
import { Backdrop, CircularProgress, Portal } from '@mui/material'
import toast from 'react-hot-toast'
import theme from 'theme'

/* Utils */
import { buildAccountList, canSwitchAccounts } from 'lib/accountList'
import { useAppSelector } from 'store/hooks'
import { accountSettingsSelector } from 'store/user/userSlice'

/* Components */
import {
  AccountLogo,
  AccountMenu,
  AccountMenuItem,
  AccountName,
  OverlayLabel,
  SwitcherItem,
  SwitcherRow
} from './accountSwitcher.styles'
import ChevronRight from '../../svg/ChevronRight'
import OnyxiaTooltip from '../../tooltip/OnyxiaTooltip'
import { HotToast } from '../../toast'

/* A function rather than a constant so a test can render the switcher with the flag both on and off.
 * The build still substitutes a fixed value here, so reading it costs nothing. */
const isAccountSwitcherEnabled = () => process.env.REACT_APP_ENABLE_ACCOUNT_SWITCHER === 'true'

/* A sign-in that never comes back would otherwise leave the operator stuck behind the overlay for
 * good. Letting them back into a possibly half-loaded app is the better of the two. */
const SWITCH_TIMEOUT_MS = 15000

/* Higher than anything else on screen, the sidebar's own permission tooltip included, because the
 * overlay has to leave nothing underneath it readable or clickable. */
const OVERLAY_Z_INDEX = 100000000001

interface Props {
  isExpanded: boolean
}

const AccountSwitcher: FC<Props> = ({ isExpanded }) => {
  const navigate = useNavigate()
  const accountSettings = useAppSelector(accountSettingsSelector)
  const { user } = useAuth()
  const { tenants } = useTenantsState()
  const { switchTenant } = useTenantsActions()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [targetTenantId, setTargetTenantId] = useState<string | null>(null)
  const settingsClearedRef = useRef(false)
  const navigatedRef = useRef(false)
  const failsafeRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const activeTenantId = (user?.tenantId as string | undefined) ?? null

  const endSwitch = useCallback(() => {
    if (failsafeRef.current) clearTimeout(failsafeRef.current)
    failsafeRef.current = null
    settingsClearedRef.current = false
    navigatedRef.current = false
    setTargetTenantId(null)
  }, [])

  const abandonSwitch = useCallback(
    (text: string, description: string) => {
      endSwitch()
      toast.error(() => <HotToast text={text} description={description} />)
    },
    [endSwitch]
  )

  useEffect(
    () => () => {
      if (failsafeRef.current) clearTimeout(failsafeRef.current)
    },
    []
  )

  /* Wait for the active account to actually change before moving anyone. It is the only proof the
   * switch happened, so one that fails or is abandoned leaves the operator on the page they chose. */
  useEffect(() => {
    if (!targetTenantId || activeTenantId !== targetTenantId || navigatedRef.current) return
    navigatedRef.current = true
    navigate('/dashboard')
  }, [targetTenantId, activeTenantId, navigate])

  /* The overlay waits for two things: the provider really switched, and the new account's settings
   * have arrived. Watching them empty out first is what makes the second check mean anything —
   * until the old account is cleared, the settings still describe the account being left. */
  useEffect(() => {
    if (!targetTenantId) return
    if (!accountSettings) {
      settingsClearedRef.current = true
      return
    }
    if (activeTenantId === targetTenantId && settingsClearedRef.current) {
      endSwitch()
    }
  }, [targetTenantId, activeTenantId, accountSettings, endSwitch])

  const accounts = useMemo(() => buildAccountList(tenants, activeTenantId), [tenants, activeTenantId])

  /* Falls back to the tenant id, because the provider can sign someone into an account that is not
   * in their own membership list, and a nameless row with a nameless tooltip tells them nothing. */
  const currentName = accounts.find((account) => account.tenantId === activeTenantId)?.name ?? activeTenantId ?? ''
  const currentLogo = tenants?.find((tenant) => tenant.tenantId === activeTenantId)?.logo

  if (!isAccountSwitcherEnabled() || !canSwitchAccounts(accounts)) return null

  const handleSelect = (tenantId: string) => {
    // The overlay blocks the mouse but not the keyboard, and a second switch leaves the first timer running.
    if (targetTenantId) return

    setAnchorEl(null)
    setTargetTenantId(tenantId)
    settingsClearedRef.current = false
    navigatedRef.current = false
    failsafeRef.current = setTimeout(
      () =>
        abandonSwitch(
          'Switching accounts is taking longer than expected',
          'Refresh the page to continue. If this keeps happening, contact support.'
        ),
      SWITCH_TIMEOUT_MS
    )

    switchTenant({
      tenantId,
      callback: (succeeded) => {
        // Must return nothing — hand the provider's SDK any value back and its loading flag never clears.
        if (!succeeded) {
          abandonSwitch(
            "Couldn't switch account",
            'Something went wrong while switching accounts. Please try again, or contact support if the problem continues.'
          )
        }
      }
    })
  }

  const row = (
    <SwitcherRow
      className="account-switcher-row"
      isexpanded={isExpanded ? 'true' : 'false'}
      aria-haspopup="true"
      aria-expanded={anchorEl ? 'true' : undefined}
      aria-label={`Current account: ${currentName}`}
      onClick={(event) => setAnchorEl(event.currentTarget)}
    >
      <AccountLogo src={currentLogo || undefined} alt={currentName}>
        {currentName.charAt(0)}
      </AccountLogo>
      {isExpanded && (
        <>
          <AccountName>{currentName}</AccountName>
          <ChevronRight />
        </>
      )}
    </SwitcherRow>
  )

  return (
    <SwitcherItem className="account-switcher-item">
      {isExpanded ? (
        row
      ) : (
        <OnyxiaTooltip title={currentName} placement="right">
          {row}
        </OnyxiaTooltip>
      )}

      <AccountMenu
        className="account-switcher-menu"
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {accounts.map((account) => (
          <AccountMenuItem
            key={account.tenantId}
            selected={account.tenantId === activeTenantId}
            disabled={account.tenantId === activeTenantId}
            onClick={() => handleSelect(account.tenantId)}
          >
            {account.name}
          </AccountMenuItem>
        ))}
      </AccountMenu>

      {/* Rendered outside the sidebar: a full-screen overlay should not depend on how the drawer
          happens to be positioned, and the footer is a list that only accepts list items. */}
      {targetTenantId && (
        <Portal>
          <Backdrop
            open
            data-testid="account-switch-overlay"
            sx={{ zIndex: OVERLAY_Z_INDEX, flexDirection: 'column', gap: `${theme.spacing.small}px` }}
          >
            <CircularProgress sx={{ svg: { circle: { stroke: theme.colors.cta } } }} />
            <OverlayLabel>Switching accounts…</OverlayLabel>
          </Backdrop>
        </Portal>
      )}
    </SwitcherItem>
  )
}

export default AccountSwitcher
